import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fetchTrustScore } from "../../lib/api";

// Colors from §4.4 with a subtle GREEN GRADIENT for the "all-green zone" (≥8):
// 9.5+ → deep green · 9.0-9.4 → medium-deep · 8.5-8.9 → medium · 8.0-8.4 → light-green
const RING_COLOR = (s) => {
  if (s >= 9.5) return "#2E7D4F";        // deep green
  if (s >= 9.0) return "#3D8F5B";        // medium-deep
  if (s >= 8.5) return "#4A9E6B";        // medium (original)
  if (s >= 8.0) return "#7AB88C";        // light green
  if (s >= 6.0) return "#C9A96E";        // gold (legacy fallback)
  return "#C47070";                       // red (legacy fallback)
};
const SCORE_COLOR = (s) => {
  if (s >= 9.5) return "#2E7D4F";
  if (s >= 9.0) return "#3D8F5B";
  if (s >= 8.5) return "#4A9E6B";
  if (s >= 8.0) return "#7AB88C";
  if (s >= 6.0) return "#B8860B";
  return "#B85450";
};
const TRACK = "#F3E8EF";
const TEXT_MAIN = "#2D1B2E";
const TEXT_SEC = "#6B5B6E";
const TEXT_MUTED = "#8B7B8E";
const CARD_BG = "#FDFBFC";
const CARD_BORDER = "#F0E4EE";
const PILL_BG = "#FDF8FC";
const PILL_BORDER = "#E8D0E8";
const PILL_TEXT = "#9B4A6B";
const AVATAR_BG = "#FDF0F5";
const AVATAR_TEXT = "#9B4A6B";

const trustFontStyle = {
  fontFamily: "'Poppins', system-ui, sans-serif",
  color: TEXT_MAIN,
};
const serifFontStyle = {
  fontFamily: "'Playfair Display', serif",
};

/* -----------------------------------------------------------
 * Compact circular badge — used on cards / product images
 * ----------------------------------------------------------- */
export const TrustBadge = ({ score, size = 44, onClick, testId = "trust-badge" }) => {
  if (score == null) return null;
  const s = Number(score);
  const ring = RING_COLOR(s);
  const inner = size - 8;
  const stroke = 3.5;
  const r = (inner - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (s / 10) * c;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={`Score de confiance ${s.toFixed(1)} sur 10 — cliquer pour détails`}
      className="trust-badge-btn"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        boxShadow: "0 3px 12px rgba(45,27,46,.12)",
        border: "none",
        padding: 0,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        transition: "transform .18s ease, box-shadow .18s ease",
      }}
    >
      <svg width={inner} height={inner} viewBox={`0 0 ${inner} ${inner}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={inner / 2} cy={inner / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
        <circle
          cx={inner / 2} cy={inner / 2} r={r} fill="none"
          stroke={ring} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <span
        style={{
          position: "absolute", ...serifFontStyle,
          fontSize: size * 0.30, color: SCORE_COLOR(s), fontWeight: 500,
          lineHeight: 1,
        }}
      >
        {s.toFixed(1)}
      </span>
    </button>
  );
};

/* -----------------------------------------------------------
 * Detailed Lightbox — score gauge + criteria + pastilles
 * ----------------------------------------------------------- */
export const TrustBadgeLightbox = ({ open, onClose, slug, initialData = null, testId = "trust-lightbox" }) => {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData && !!slug);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open || initialData || !slug) return;
    setLoading(true);
    fetchTrustScore(slug)
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, slug, initialData]);

  // Animate progress after mount
  useEffect(() => {
    if (!open || !data) { setProgress(0); return; }
    const target = Number(data.global_score || 0);
    let start;
    const dur = 1200;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / dur);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setProgress(target * eased);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [open, data]);

  // Escape key handler
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={onClose}
      data-testid={testId}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,.4)", backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "trustFadeIn .25s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true"
        style={{
          background: "#FFFFFF", borderRadius: 14, maxWidth: 620, width: "100%",
          maxHeight: "92vh", overflowY: "auto",
          boxShadow: "0 24px 60px rgba(45,27,46,.28)",
          animation: "trustRise .35s cubic-bezier(.2,.7,.3,1)",
          ...trustFontStyle,
        }}
      >
        {loading || !data ? (
          <div style={{ padding: 40, textAlign: "center", color: TEXT_SEC }}>
            Chargement de l&apos;analyse…
          </div>
        ) : (
          <BadgeContent data={data} progress={progress} onClose={onClose} />
        )}
      </div>

      <style>{`
        @keyframes trustFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes trustRise {
          from { opacity: 0; transform: translateY(20px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .trust-crit-card { animation: trustRise .5s ease both; }
      `}</style>
    </div>,
    document.body
  );
};

const BadgeContent = ({ data, progress, onClose }) => {
  const s = Number(data.global_score || 0);
  const ring = RING_COLOR(s);
  const scoreCol = SCORE_COLOR(s);
  const barPct = (progress / 10) * 100;
  const criteria = data.criteria_labels || [];
  const scores = data.scores || {};

  // Ring SVG geometry (72px)
  const R = 30, ST = 5;
  const CC = 2 * Math.PI * R;
  const dash = (progress / 10) * CC;

  return (
    <>
      {/* Header */}
      <div style={{ padding: "20px 24px 12px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div style={{
          width: 44, height: 44, borderRadius: 999, background: AVATAR_BG,
          color: AVATAR_TEXT, display: "flex", alignItems: "center", justifyContent: "center",
          ...serifFontStyle, fontSize: 22, fontWeight: 600, flexShrink: 0,
        }}>B</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...serifFontStyle, fontSize: 17, color: TEXT_MAIN, fontWeight: 600, lineHeight: 1.2 }}>
            Beckyliss Cosmetics
          </div>
          <div style={{ fontSize: 11.5, color: TEXT_SEC, marginTop: 2, textTransform: "uppercase", letterSpacing: ".08em" }}>
            Analyse de formulation — {data.sub_category || data.family || "produit"}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Fermer"
          data-testid="trust-lightbox-close"
          style={{
            width: 32, height: 32, borderRadius: 999, border: "none",
            background: "transparent", color: TEXT_SEC, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Main score */}
      <div style={{ padding: "22px 24px 18px", display: "flex", alignItems: "center", gap: 20 }}>
        <div style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
          <svg width="72" height="72" viewBox="0 0 72 72" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="36" cy="36" r={R} fill="none" stroke={TRACK} strokeWidth={ST} />
            <circle
              cx="36" cy="36" r={R} fill="none"
              stroke={ring} strokeWidth={ST} strokeLinecap="round"
              strokeDasharray={`${dash} ${CC - dash}`}
            />
          </svg>
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ ...serifFontStyle, fontSize: 22, color: scoreCol, fontWeight: 500, lineHeight: 1 }}>
              {progress.toFixed(1)}
            </span>
            <span style={{ fontSize: 9, color: TEXT_MUTED, marginTop: 1 }}>/10</span>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...serifFontStyle, fontSize: 18, color: TEXT_MAIN, fontWeight: 600 }}>
            {data.label}
          </div>
          <div style={{ fontSize: 11.5, color: TEXT_SEC, marginTop: 4, lineHeight: 1.45 }}>
            {data.label_description}
          </div>
          <div style={{ marginTop: 10, height: 8, borderRadius: 999, background: TRACK, overflow: "hidden" }}>
            <div style={{
              width: `${barPct}%`, height: "100%", background: ring,
              transition: "width 1.2s ease-out", borderRadius: 999,
            }} />
          </div>
        </div>
      </div>

      {/* Criteria grid */}
      <div style={{ padding: "6px 24px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {criteria.map((c, i) => {
          const val = Number(scores[c.key] ?? 0);
          const label = criterionLabel(val);
          const col = RING_COLOR(val);
          return (
            <div
              key={c.key}
              className="trust-crit-card"
              style={{
                background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 10,
                padding: "10px 12px", animationDelay: `${i * 0.08}s`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: TEXT_MAIN, letterSpacing: ".01em" }}>
                  {c.label}
                </span>
                <span style={{ fontSize: 10, color: col, fontWeight: 600 }}>{label}</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: TRACK, overflow: "hidden" }}>
                <div style={{
                  width: `${(val / 10) * 100}%`, height: "100%", background: col,
                  transition: "width .8s ease-out", borderRadius: 999,
                }} />
              </div>
              <div style={{ fontSize: 10, color: TEXT_MUTED, marginTop: 4 }}>
                {val}/10
              </div>
            </div>
          );
        })}
      </div>

      {/* Pills */}
      {data.pills && data.pills.length > 0 && (
        <>
          <div style={{ height: 1, background: CARD_BORDER, margin: "0 24px" }} />
          <div style={{ padding: "14px 24px", display: "flex", flexWrap: "wrap", gap: 8 }}>
            {data.pills.map((p) => (
              <span key={p} style={{
                background: PILL_BG, border: `1px solid ${PILL_BORDER}`, color: PILL_TEXT,
                borderRadius: 999, padding: "5px 12px", fontSize: 10.5, fontWeight: 500,
                letterSpacing: ".02em",
              }}>{p}</span>
            ))}
          </div>
        </>
      )}

      {/* Alerts */}
      {data.blocking_alert && (
        <div style={{
          margin: "0 24px 12px", padding: "10px 12px", borderRadius: 10,
          background: "#FFF3F3", border: "1px solid #F3C7C7", color: "#8B3A3A",
          fontSize: 11, lineHeight: 1.4,
        }}>
          ⚠️ {data.blocking_alert}
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: "10px 24px 20px", textAlign: "center", fontSize: 9.5, color: TEXT_MUTED, lineHeight: 1.5 }}>
        {footerText(data)}
      </div>
    </>
  );
};

function criterionLabel(v) {
  if (v >= 9) return "Excellent";
  if (v >= 7) return "Très bien";
  if (v >= 5) return "Bien";
  if (v >= 3) return "Moyen";
  return "Faible";
}
function footerText(d) {
  if (d.grid === "complement") {
    return "Analyse basée sur la composition nutritionnelle — Conforme à la directive 2002/46/CE. Ne se substitue pas à une alimentation variée et équilibrée.";
  }
  if (!d.has_inci) {
    return "Analyse partielle — composition complète non communiquée par le fabricant.";
  }
  return "Analyse basée sur la composition INCI — Dossier de sécurité EU établi.";
}

export default TrustBadge;
